import _ from "lodash";
import React, { useEffect, useState } from "react";
import { Alert as Alert2, Card, CardBody, Col, Row, Table } from "reactstrap";
import { DesktopContainer } from "../layouts";
import { api, filterHelpers, helpers } from "../utils";
import {
  FilterableScreen, FilterSelect, FilterText,
  ItemDropdownMenu, Pager, ServiceThreadSpinner
} from "./";

const THREADS_LOAD_FAILED_MESSAGE = "The threads could not be loaded. If this persists, contact a system admin.";
// The screen loads twice on open (after the drop-downs, and on its filters), so a failure would otherwise toast twice.
const THREADS_LOAD_TOAST_ID = "thread-finder-load";
const EMPTY_PAGE = { list: [], pageNumber: 1, totalPages: 1 };

export default function ThreadFinder(props) {
  const [colorOptions, setColorOptions] = useState([]);
  const [filters, setFilters] = useState([]);  
  const [loading, setLoading] = useState(false);
  const [materialOptions, setMaterialOptions] = useState([]);
  const [message, setMessage] = useState(null);
  const [needleSizeOptions, setNeedleSizeOptions] = useState([]);
  const [threadSizeOptions, setThreadSizeOptions] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [paginatedList, setPaginatedList] = useState(EMPTY_PAGE);

  // the queries on these are currently not using an id or id equivalent, so may need to
  // adjust the shape of these reference lists once these tables are in the db
  useEffect(() => {
    document.title = `Thread Finder`
    setLoading(false);
    let apiCalls = [];
    apiCalls.push(
      api
        .getThreadSizes()
        .then((response) => {
          return {
            threadSizeList: _.map(
              helpers.addLabelValueToList(response.threadSizes),
              (m) => {
                m.value = m.name;
                m.id = m.name;
                return m;
              }
            ),
          };
        })
        .catch(api.catchHandler)
    );
    apiCalls.push(
      api
        .getThreadColors()
        .then((response) => {
          return {
            threadColorList: _.map(
              helpers.addLabelValueToList(response.threadColors),
              (m) => {
                m.value = m.name;
                m.id = m.name;
                return m;
              }
            ),
          };
        })
        .catch(api.catchHandler)
    );
    apiCalls.push(
      api
        .getThreadMaterials()
        .then((response) => {
          return {
            threadMaterialList: _.map(
              helpers.addLabelValueToList(response.threadMaterials),
              (m) => {
                m.value = m.code;
                m.id = m.code;
                return m;
              }
            ),
          };
        })
        .catch(api.catchHandler)
    );
    apiCalls.push(
      api
        .getNeedleSizes()
        .then((response) => {
          return {
            needleSizeList: helpers.addLabelValueToList(response.needleSizes),
          };
        })
        .catch(api.catchHandler)
    );
    Promise.all(apiCalls)
      .then((arrayResults) => {
        let aggResults = {};
        _.each(arrayResults, (x) => Object.assign(aggResults, x));
        setThreadSizeOptions(
          aggResults.threadSizeList ? aggResults.threadSizeList : []
        );
        setColorOptions(
          aggResults.threadColorList ? aggResults.threadColorList : []
        );
        setMaterialOptions(
          aggResults.threadMaterialList ? aggResults.threadMaterialList : []
        );
        setNeedleSizeOptions(
          aggResults.needleSizeList ? aggResults.needleSizeList : []
        );
      })
      .catch((error) =>
        setMessage({
          flavor: "alert-danger",
          text: "There was an error loading the drop down list data",
        })
      )
      .finally(() => refreshData());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters,
    // sortField, sortDirection, 
    pageNumber]);

  function createPayload() {
    let payload = {
      sortField: null,
      sortDirection: null,
      pageNumber: pageNumber,
    };
    _.map(filters, (filter) => (payload[filter.filterName] = filter.value));
    if (payload.MinTensileStrength
      || payload.MinTensileStrength === '') {
      handleNumberFilter(payload, "MinTensileStrength", payload.MinTensileStrength);
    }
    if (payload.MaxTensileStrength
      || payload.MaxTensileStrength === '') {
      handleNumberFilter(payload, "MaxTensileStrength", payload.MaxTensileStrength);
    }
    return payload;
  }

  function handleNumberFilter(payload, field, value) {
    let resolvedValue = parseFloat(value);
    if (!isNaN(resolvedValue)) {
      payload[field] = resolvedValue;
    }
    else {
      delete payload[field];
    }
  }

  function onFilterChange(changedFilter) {
    setPageNumber(1);
    const filterChanges = filterHelpers.getFilters(filters, changedFilter);
    setFilters(filterChanges);
  }

  function refreshData() {
    setMessage(null);
    api
      .post("SewingTech/GetThreads", createPayload())
      .then((response) => {
        // On a failure show no threads rather than the previous search's as this one's results.
        if (api.toastFailure(response, THREADS_LOAD_FAILED_MESSAGE, THREADS_LOAD_TOAST_ID)) {
          setPaginatedList(EMPTY_PAGE);
          return;
        }
        setPaginatedList(response.data.list);
      })
      .catch(api.catchHandler);
  }

  return (
    <DesktopContainer screenName="Thread Finder">
      <FilterableScreen
        filters={
          <div className="filter-header mb-3">
            <Row className="justify-content-center pe-2">
              <Col xs={6}>
                <Row className="align-items-center my-1">
                  <Col xs={3}>
                    <h5 children="Material:" className="float-end text-white" />
                  </Col>
                  <Col xs={8}>
                    <FilterSelect
                      filterName="Material"
                      displayName="Material"
                      hideDisplayName
                      value={filterHelpers.getValue(
                        filters,
                        "Material"
                      )}
                      onChangeCallback={(e) => onFilterChange(e)}
                      options={materialOptions}
                      isClearable
                    />
                  </Col>
                </Row>
                <Row className="align-items-center my-1">
                  <Col xs={3}>
                    <h5 children="Color:" className="float-end text-white" />
                  </Col>
                  <Col xs={8}>
                    <FilterSelect
                      filterName="Color"
                      displayName="Color"
                      hideDisplayName
                      value={filterHelpers.getValue(filters, "Color")}
                      onChangeCallback={(e) => onFilterChange(e)}
                      options={colorOptions}
                      isClearable
                    />
                  </Col>
                </Row>
                <Row className="align-items-center my-1">
                  <Col xs={3}>
                    <h5 children="Pkg Size:" className="float-end text-white" />
                  </Col>
                  <Col xs={8}>
                    <FilterText
                      filterName="PackageSize"
                      displayName="Package Size"
                      hideDisplayName
                      value={filterHelpers.getValue(
                        filters,
                        "PackageSize"
                      )}
                      onChangeCallback={(e) => onFilterChange(e)}
                      isClearable
                    />
                  </Col>
                </Row>
                <Row className="align-items-center my-1">
                  <Col xs={3}>
                    <h5
                      children="~ Tensile #:"
                      className="float-end text-white"
                    />
                  </Col>
                  <Col xs={3}>
                    <FilterText
                      filterName="MinTensileStrength"
                      displayName="Min. Tensile Strength"
                      hideDisplayName
                      value={filterHelpers.getValue(
                        filters,
                        "MinTensileStrength"
                      )}
                      onChangeCallback={(e) => onFilterChange(e)}
                      isClearable
                    />
                  </Col>
                  <Col xs={2}>
                    <h5 children="To:" className="float-end text-white" />
                  </Col>
                  <Col xs={3}>
                    <FilterText
                      filterName="MaxTensileStrength"
                      displayName="Max. Tensile Strength"
                      hideDisplayName
                      value={filterHelpers.getValue(
                        filters,
                        "MaxTensileStrength"
                      )}
                      onChangeCallback={(e) => onFilterChange(e)}
                    />
                  </Col>
                </Row>
              </Col>
              <Col xs={6}>
                <Row className="align-items-center my-1">
                  <Col xs={3}>
                    <h5
                      children="Thread Size:"
                      className="float-end text-white"
                    />
                  </Col>
                  <Col xs={8}>
                    <FilterSelect
                      filterName="ThreadSize"
                      displayName="Thread Size"
                      hideDisplayName
                      value={filterHelpers.getValue(
                        filters,
                        "ThreadSize"
                      )}
                      onChangeCallback={(e) => onFilterChange(e)}
                      options={threadSizeOptions}
                      isClearable
                    />
                  </Col>
                </Row>
                <Row className="align-items-center my-1">
                  <Col xs={3}>
                    <h5
                      children="Needle Size:"
                      className="float-end text-white"
                    />
                  </Col>
                  <Col xs={8}>
                    <FilterSelect
                      filterName="NeedleSize"
                      displayName="Needle Size"
                      hideDisplayName
                      value={filterHelpers.getValue(
                        filters,
                        "NeedleSize"
                      )}
                      onChangeCallback={(e) => onFilterChange(e)}
                      options={needleSizeOptions}
                      isClearable
                    />
                  </Col>
                </Row>
                <Row className="align-items-center my-1 pt-2">
                  <Col xs={12}>
                    <i>
                      <h5
                        children="(8OZ, 5LB, KS1, KS2, FC etc)"
                        className="float-start text-white fontSize85"
                      />
                    </i>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        }
        pager={
          <div className="float-start">
            <Pager
              pageNumber={
                paginatedList?.pageNumber ? paginatedList.pageNumber : 0
              }
              totalPages={
                paginatedList?.totalPages ? paginatedList.totalPages : 0
              }
              callBack={(newPageNumber) => setPageNumber(newPageNumber)}
            />
          </div>
        }
      >
        <ServiceThreadSpinner on={loading}>
          <Card>
            <CardBody>
              <Row className="p-0 m-0">
                <Col xs="12">
                  {message ? (
                    <Alert2 className={message.flavor}>{message.text}</Alert2>
                  ) : null}
                  <Table
                    striped
                    hover
                    size="sm"
                    responsive={false}
                    id="userTable"
                  >
                    <thead>
                      <tr>
                        <th width="33%">Item</th>
                        <th>Material</th>
                        <th>Color</th>
                        <th>Thread Size</th>
                        <th>~ Tensile #</th>
                        <th>Sugg. Needle Size</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedList &&
                        paginatedList.list &&
                        paginatedList.list.length > 0
                        ? _.map(paginatedList.list, (t) => (
                          <tr key={`thread-${t.itemNumber}`}>
                            <ItemDropdownMenu
                              itemNumber={t.itemNumber}
                              itemDescription={t.itemWithDesc}
                              attachmentCount={t.itemAttachmentCount}
                            />
                            <td>{t.material}</td>
                            <td>{t.color}</td>
                            <td>{t.threadSize}</td>
                            <td>{t.strength}</td>
                            <td>{t.needleSizeDescription}</td>
                          </tr>
                        ))
                        : null}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </ServiceThreadSpinner>
      </FilterableScreen>
    </DesktopContainer>
  );
}
